// Annual vehicle license fee (אגרת רישוי שנתית) calculator.
// Fee tables taken from the official gov.il page (updated from 1 April 2026):
// https://www.gov.il/he/pages/drivers-car-license-fee-boards
//
// Fee inputs come from the data.gov.il vehicle registry record:
//   - kvutzat_agra_cd : fee group 1-7 (private/commercial vehicles up to 3.5t)
//   - shnat_yitzur    : production year (selects the age tier column)
//   - mishkal_kolel   : total weight (trucks)
//   - nefah/nefach_manoa + sug_delek_nm : engine cc / fuel (motorcycles)

// Private & commercial vehicles up to 3,500 kg:
// fee by group (1-7) x registration-year tier.
// Tiers: [2024-2026, 2021-2023, 2017-2020, 2016 and older]
const PRIVATE_FEE_TABLE = {
  1: [1266, 1109, 972, 849],
  2: [1610, 1404, 1230, 1076],
  3: [1941, 1698, 1487, 1297],
  4: [2315, 1968, 1674, 1422],
  5: [2651, 2184, 1802, 1490],
  6: [3764, 2823, 2117, 1585],
  7: [5364, 3753, 2627, 1840],
};

const privateYearTier = (year) => {
  if (!year) return null;
  const y = Number(year);
  if (y >= 2024) return 0;
  if (y >= 2021) return 1;
  if (y >= 2017) return 2;
  return 3;
};

// Trucks (רכב משא) - diesel, by total weight in kg.
const truckDieselFee = (weight) => {
  const w = Number(weight);
  if (!w) return null;
  if (w <= 16000) return 2292;
  if (w <= 20000) return 2877;
  return 3964;
};

// Trucks running on gasoline pay a flat fee.
const TRUCK_GASOLINE_FEE = 440;

// Motorcycles, by engine cc (electric pays the minimum).
const bikeFee = (cc, fuelName) => {
  if ((fuelName || "").includes("חשמל")) return 52;
  const c = Number(cc);
  if (!c) return null;
  if (c <= 50) return 52;
  if (c <= 150) return 194;
  return 357;
};

/**
 * Calculate the annual license fee for a vehicle.
 * @param {"CAR"|"TRUCK"|"BIKE"} vehicle - vehicle type tag from the license search
 * @param {object} licenseData - raw registry record (data.gov.il fields)
 * @returns {{ amount: number, note: string } | null} null when fee can't be determined
 */
export const calcLicenseFee = (vehicle, licenseData) => {
  if (!licenseData) return null;

  // The gov WLTP registry spells the fee-group field "kvuzat_agra_cd"
  // (without a t); support both spellings.
  const feeGroup = Number(
    licenseData.kvuzat_agra_cd ?? licenseData.kvutzat_agra_cd
  );

  // Infer the vehicle type when not passed explicitly:
  // a fee group means a private/commercial car; weight above 3.5t means a truck.
  if (!vehicle) {
    if (PRIVATE_FEE_TABLE[feeGroup]) {
      vehicle = "CAR";
    } else if (Number(licenseData.mishkal_kolel) > 3500) {
      vehicle = "TRUCK";
    } else {
      return null;
    }
  }

  if (vehicle === "TRUCK") {
    const fuel = licenseData.sug_delek_nm || "";
    if (fuel.includes("בנזין")) {
      return { amount: TRUCK_GASOLINE_FEE, note: "רכב משא בנזין" };
    }
    const amount = truckDieselFee(licenseData.mishkal_kolel);
    return amount ? { amount, note: "רכב משא לפי משקל כולל" } : null;
  }

  if (vehicle === "BIKE") {
    const amount = bikeFee(
      licenseData.nefah_manoa || licenseData.nefach_manoa,
      licenseData.sug_delek_nm
    );
    return amount ? { amount, note: "אופנוע לפי נפח מנוע" } : null;
  }

  // Default: private/commercial car by fee group + year
  const tier = privateYearTier(licenseData.shnat_yitzur);
  if (!PRIVATE_FEE_TABLE[feeGroup] || tier === null) return null;
  return {
    amount: PRIVATE_FEE_TABLE[feeGroup][tier],
    note: `קבוצת אגרה ${feeGroup}`,
  };
};

export const formatFee = (amount) =>
  `₪${Number(amount).toLocaleString("he-IL")}`;
